
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { ContractorsGrid } from "@/components/contractors-list/ContractorsGrid";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { Leaf, WrenchIcon, ClipboardCheck, ScrollText, Shield, Clock, CheckCircle } from "lucide-react";

const BestLandscapersPage = () => {
  const breadcrumbItems = [
    { label: "Best Landscapers in OKC" }
  ];

  const { data: contractors = [], isLoading } = useQuery<Contractor[]>({
    queryKey: ['contractors', 'landscaping'],
    queryFn: async () => {
      console.log('Fetching landscaping contractors...');
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Landscaping')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });
      
      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }
      return data || [];
    }
  });

  return (
    <div className="min-h-screen">
      <SEO
        title="Best Landscapers in OKC | Home Pros OKC"
        description="Looking for the best landscaping services in OKC? Our trusted pros handle lawn care, hardscaping, irrigation systems & landscape design. Compare top-rated landscapers today!"
        canonicalUrl="/best-landscapers-okc"
      />
      
      <div className="bg-gradient-to-b from-primary/20 to-background py-16">
        <div className="container mx-auto px-4">
          <Breadcrumbs items={breadcrumbItems} />
          
          <div className="text-center max-w-3xl mx-auto">
            <Leaf className="w-16 h-16 mx-auto text-primary mb-6" />
            <h1 className="text-4xl font-bold mb-6">Best Landscapers in OKC</h1>
            <p className="text-lg text-muted-foreground">
              Transform your outdoor space with Oklahoma City's top landscaping professionals. Our network of experienced landscapers provides expert design, installation, and maintenance services to enhance your property's beauty and value.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Services Section with Icons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <WrenchIcon className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Expert Design</h3>
              <p className="text-muted-foreground">Custom landscape planning</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <Clock className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Seasonal Service</h3>
              <p className="text-muted-foreground">Year-round lawn maintenance</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <Shield className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Quality Materials</h3>
              <p className="text-muted-foreground">Premium plants and products</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-6 bg-card rounded-lg shadow-sm">
            <ClipboardCheck className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Sustainable Practices</h3>
              <p className="text-muted-foreground">Eco-friendly solutions</p>
            </div>
          </div>
        </div>

        {/* Core Services Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-center">Landscaping Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 max-w-4xl mx-auto">
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Landscape design & installation</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Lawn care & maintenance</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Irrigation systems</span>
              </li>
            </ul>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Hardscaping & paver patios</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Outdoor living spaces</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Seasonal planting & cleanup</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mb-12 bg-card p-8 rounded-lg">
          <h2 className="text-2xl font-semibold mb-6 text-center">Questions to Ask Your Landscaper</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 max-w-4xl mx-auto">
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Do you offer 3D design services?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>What's your warranty on plants?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Do you offer maintenance plans?</span>
              </li>
            </ul>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>How do you handle drainage issues?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Are your crews trained and insured?</span>
              </li>
              <li className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-primary" />
                <span>Do you use native plants for sustainability?</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Contractors List Section */}
        <div>
          <h2 className="text-2xl font-semibold mb-6 text-center">Top Landscaping Companies in Oklahoma City</h2>
          {!isLoading && contractors.length > 0 ? (
            <ContractorsGrid contractors={contractors} viewMode="grid" />
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {isLoading ? "Loading contractors..." : "No contractors found."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BestLandscapersPage;
